/**
 * WidgetShell — chrome compartilhado dos widgets (A3.3 + A3.5).
 *
 * Container com header (icon + nome + descrição opcional) + body slot.
 * Empty/loading/error states em variants de cores.
 *
 * A3.5 (Master Plan A3 sub-fase 5):
 *   - Suporte dark mode (paper-dark / ink-dark / line-soft-dark)
 *   - Mantém estrutura — só substitui cores hardcoded por tokens do design system
 */
import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface WidgetShellProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  loading?: boolean;
  error?: string | null;
  empty?: boolean;
  emptyLabel?: string;
  children?: ReactNode;
  className?: string;
  testId?: string;
}

export function WidgetShell(props: WidgetShellProps) {
  return (
    <div
      className={`w-full h-full bg-paper-surface dark:bg-paper-dark-surface border border-line-soft dark:border-line-soft-dark rounded-lg shadow-sm flex flex-col overflow-hidden ${props.className ?? ''}`}
      data-testid={props.testId}
    >
      <div className="px-3 py-2 border-b border-line-soft dark:border-line-soft-dark flex items-center gap-2">
        {props.icon && (
          <span className="text-ink-3 dark:text-ink-dark-3 shrink-0">{props.icon}</span>
        )}
        <div className="min-w-0">
          <div className="text-xs font-semibold truncate text-ink-1 dark:text-ink-dark-1">
            {props.title}
          </div>
          {props.subtitle && (
            <div className="text-xs text-ink-3 dark:text-ink-dark-3 truncate">
              {props.subtitle}
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {props.loading ? (
          <Skeleton />
        ) : props.error ? (
          <ErrorState message={props.error} />
        ) : props.empty ? (
          <EmptyState label={props.emptyLabel} />
        ) : (
          props.children
        )}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="h-full p-3 space-y-2 animate-pulse" data-testid="widget-skeleton">
      <div className="h-3 bg-line-soft dark:bg-paper-dark-warm rounded w-3/4" />
      <div className="h-3 bg-line-soft dark:bg-paper-dark-warm rounded w-1/2" />
      <div className="h-3 bg-line-soft dark:bg-paper-dark-warm rounded w-2/3" />
    </div>
  );
}

function ErrorState(props: { message: string }) {
  return (
    <div
      className="h-full p-3 flex items-center justify-center gap-1.5 text-center text-xs text-red dark:text-red-glow"
      data-testid="widget-error"
    >
      <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden />
      <span>{props.message}</span>
    </div>
  );
}

function EmptyState(props: { label?: string }) {
  return (
    <div
      className="h-full p-3 flex items-center justify-center text-xs text-ink-3 dark:text-ink-dark-3 italic"
      data-testid="widget-empty"
    >
      {props.label ?? 'Sem dados'}
    </div>
  );
}

// FASE-C: `WidgetPendingBackend` foi REMOVIDO. Era a casca com selo "beta" e
// 🚧 "Widget aguarda implementação backend" usada por 11 widgets sem endpoint —
// a Personalização da Home não pode oferecer o que não existe.
